import { Button } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

import {
  createAtHomeModule,
  createAtHomePricing,
  deleteAtHomeModule,
  deleteAtHomePricing,
  fetchAtHomeModules,
  fetchAtHomePricing,
  updateAtHomeModule,
  updateAtHomePricing,
  createAtHomeApartment,
  createAtHomeCategory,
  fetchAtHomeApartments,
  fetchAtHomeCategories,
  fetchAtHomeCategoryPages,
  updateAtHomeApartment,
  updateAtHomeCategory
} from 'api/atHome'

import ApartmentCategoriesDialog from './ApartmentCategoriesDialog'
import {
  CATEGORY_STATUSES,
  MODULE_STATUSES,
  freqLabel,
  statusLabel,
  categoryLabel,
  GRADES
} from './format'
import SessionPlanField from './SessionPlanField'
import SetupList from './SetupList'
import CategoryPreview from './shared/CategoryPreview'
import ProjectPreview from './shared/ProjectPreview'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  icon: { width: 28, height: 28, objectFit: 'contain', display: 'block' },
  iconNone: { color: theme.palette.text.disabled }
}))

const yesNo = (v) => (v ? 'Yes' : 'No')

/**
 * Reference data behind the sessions, as two Setup tabs (owner, 2026-10-03):
 * the categories we teach and the apartments we serve. Apartments stay
 * editable here for now; the business expects pincode-based serviceability
 * later.
 */
/** Setup → Apartments tab: the venues, each with its offered categories. */
export const AtHomeApartmentsSetup = () => {
  const classes = useStyles()
  const [catsFor, setCatsFor] = React.useState(null)
  const [reloadApts, setReloadApts] = React.useState(null)
  const closeCats = () => {
    setCatsFor(null)
    if (reloadApts) reloadApts()
  }
  return (
    <div className={classes.root}>
      <SetupList
        hint="The buildings we teach at. A parent picks one of these when they set their home address. The building name is what a parent reads on the address line of an opened session, and what the teacher sees on their day list. “Subjects run here” sets which trial times a building offers — switching a subject off there does not take it off the Home screen, and does not touch the weekly Projects or Monthly slots."
        entityLabel="Apartment"
        fetchList={fetchAtHomeApartments}
        createItem={createAtHomeApartment}
        updateItem={updateAtHomeApartment}
        columns={[
          { name: 'name', label: 'Building' },
          { name: 'area', label: 'Area (shown under the name)' },
          { name: 'pincode', label: 'Pincode (shown next to the area)' },
          {
            name: 'latitude',
            label: 'Map pin',
            render: (r) => (r.latitude && r.longitude ? 'Set' : '—')
          },
          {
            name: 'categories',
            label: 'Subjects run here',
            wrap: true,
            render: (r) =>
              r.disabledCategories?.length
                ? (r.categories || []).join(', ') || 'None'
                : 'All'
          },
          {
            name: 'active',
            label: 'Active',
            render: (r) => yesNo(r.active)
          }
        ]}
        rowAction={(r, reload) => (
          <Button
            size="small"
            color="primary"
            onClick={() => {
              setReloadApts(() => reload)
              setCatsFor(r)
            }}
          >
            Subjects here
          </Button>
        )}
        fields={[
          {
            name: 'aptIntro',
            type: 'note',
            label:
              'A parent picks their building from this list when they set their home address. Under the name they read the area and the pincode together on one grey line — “Whitefield · 560066”.'
          },
          { name: 'name', label: 'Building name', type: 'text' },
          { name: 'area', label: 'Area or locality', type: 'text' },
          { name: 'pincode', label: 'Pincode', type: 'text' },
          // Optional, and the note above them says so. Used only for the
          // teacher-tracking map; a building without them books and schedules
          // exactly as before, and the apartments endpoint does not even send
          // them, so they never reach the address picker. Paste from Google
          // Maps: right-click the building, the first line is "lat, lng".
          {
            name: 'mapPinNote',
            type: 'note',
            label:
              'Map pin (optional). Parents never see these two numbers. They are the spot the teacher’s “~14 min · arriving around 6:34 pm” line counts down to on the day of a session, and only for a parent who has not dropped their own pin. To fill them in, right-click the building in Google Maps and paste the two numbers it shows.'
          },
          {
            name: 'latitude',
            label: 'Map pin — latitude (optional)',
            type: 'text'
          },
          {
            name: 'longitude',
            label: 'Map pin — longitude (optional)',
            type: 'text'
          },
          {
            name: 'active',
            label:
              'Offered to parents — untick and the building leaves the address list, and no new session or weekly slot can be put there',
            type: 'checkbox'
          }
        ]}
        emptyValues={{ active: true }}
      />
      <ApartmentCategoriesDialog apartment={catsFor} onClose={closeCats} />
    </div>
  )
}

/** Setup → Categories tab: what we teach. */
export const AtHomeCategoriesSetup = () => {
  const classes = useStyles()

  // The page list comes from the backend (constants.CategoryPage) rather than
  // being spelled out here, so the dropdown can't drift from the validator that
  // rejects an unknown key. Blank is a real choice: it means the generic page.
  const [pages, setPages] = React.useState([])
  React.useEffect(() => {
    fetchAtHomeCategoryPages()
      .then(setPages)
      .catch(() => setPages([]))
  }, [])
  // Blank already IS the generic page, so the backend's own 'generic' entry is
  // dropped here — listing both offered the same thing twice under two names.
  const pageOptions = React.useMemo(
    () => [
      { value: '', label: 'Generic page (default)' },
      ...pages.filter((p) => p.value !== 'generic')
    ],
    [pages]
  )
  const pageLabel = React.useCallback(
    (key) =>
      !key || key === 'generic'
        ? 'Generic'
        : pages.find((p) => p.value === key)?.label || key,
    [pages]
  )

  return (
    <div className={classes.root}>
      <SetupList
        /* Trimmed with the form, 2026-10-19: the sentence about what a parent
           reads was repeating what the form's own help and its preview now
           show. What is left is the bit you need BEFORE opening a row. */
        hint="What we teach. Each one is a card on the parent's Home screen that opens a trial page. Open = parents can book it; Upcoming = greyed out as “Coming soon”; Inactive = gone from the app."
        entityLabel="Category"
        // The dialog carries a second column now, so it needs the wider size
        // the module editor uses; at 'sm' the card and the fields fight.
        formMaxWidth="md"
        formPreview={(values) => <CategoryPreview values={values} />}
        fetchList={fetchAtHomeCategories}
        createItem={createAtHomeCategory}
        updateItem={updateAtHomeCategory}
        columns={[
          {
            // Was the Alfred-only icon, which is the field this round
            // removed -- and a column is the only thing that ever read it.
            // The photo takes its place: same job in the list (so a row is
            // recognisable at a glance), except this one is the picture a
            // parent actually sees on the card.
            name: 'hero_url',
            label: 'Photo',
            render: (r) =>
              r.hero_url ? (
                <img src={r.hero_url} alt="" className={classes.icon} />
              ) : (
                <span className={classes.iconNone}>app’s</span>
              )
          },
          { name: 'name', label: 'Category' },
          {
            name: 'style',
            label: 'Sold as',
            render: (r) => (r.style === 'regular' ? 'Monthly' : 'Projects')
          },
          {
            name: 'page_key',
            label: 'Page',
            render: (r) => pageLabel(r.page_key)
          },
          { name: 'sort_order', label: 'Order', align: 'right' },
          {
            name: 'status',
            label: 'Status',
            render: (r) => statusLabel(CATEGORY_STATUSES, r.status)
          }
        ]}
        /*
         * Rewritten 2026-10-19. The owner's standard for this round: "make the
         * forms cleaner, simpler and more non-technical."
         *
         * Three fields are GONE because nothing reads them — verified against
         * the parent app and then against prod, where all three are empty on
         * all six categories, so no one loses anything they typed:
         *   icon_url     drawn only in this page's own Icon column
         *   tagline      a fallback the app can never reach (the term before
         *                it is always set)
         *   description  served, but no screen renders it
         * The database columns stay, exactly as `tags` did, so this is
         * reversible without a migration.
         *
         * Five paragraph-long `note` blocks are gone too. They were the real
         * reason the form was hard to read: nine inputs buried under five
         * blocks of prose. Each field now carries ONE short line of help under
         * it (`help`), which is the normal shape of a form. Anything those
         * paragraphs explained that can be SEEN is no longer written down at
         * all — the preview beside the form shows the card, the photo, and
         * what Open / Upcoming / Inactive each look like, which is why the
         * status paragraph could go entirely.
         *
         * Labels are the words the team uses, not the field names: "Name on
         * the card", "Photo on the card", "Where it sits on Home".
         */
        fields={[
          {
            name: 'name',
            label: 'Name on the card',
            type: 'text'
          },
          {
            name: 'hero_url',
            label: 'Photo on the card',
            type: 'image',
            uploadLabel: 'photo'
          },
          {
            name: 'trial_name',
            label: 'Trial class name',
            type: 'text'
          },
          {
            name: 'trial_discount',
            label: 'Discount off the trial price (₹)',
            // A RUPEE FIGURE, not a percentage (owner). Typed once here and it
            // applies to every trial slot of this subject. Enter the full
            // price and the trial becomes free: the parent sees the old price
            // struck through and books without a payment step. The help line
            // that said so was removed from the form on 2026-10-22; the
            // behaviour is unchanged, so it is recorded here instead.
            type: 'number'
          },
          {
            name: 'page_key',
            label: 'Write-up a parent reads',
            type: 'select',
            // This chooses the BUILT-IN write-up: the wording on the trial and
            // subject pages, and which projects are listed. Generic = neutral
            // wording and no projects.
            //
            // Worth keeping even though the help line is gone (2026-10-22): an
            // earlier wording claimed only "the subject page", which was the
            // bug the owner hit -- a second STEAM showed Steam's projects and
            // the GENERIC trial page, because the trial page joined on the slug
            // instead of this field. It joins on this now
            // (at-home-app/src/catalogue/trials/content.ts).
            options: pageOptions
          },
          {
            name: 'style',
            label: 'How parents buy it after the trial',
            type: 'select',
            options: [
              {
                value: 'module',
                label: 'Projects — 8 sessions, once or twice a week'
              },
              { value: 'regular', label: 'Monthly — booked month by month' }
            ]
          },
          {
            // AFTER "How parents buy it" (owner, 2026-10-22): that field is
            // what decides whether this one appears at all, so switching a
            // subject to Monthly makes the box turn up directly underneath.
            // Monthly only -- a project subject writes this per session under
            // Setup -> Projects, where it is already a column.
            //
            // No `help`: this form carries none (2026-10-22).
            name: 'session_description',
            label: 'What happens in every class',
            type: 'text',
            showIf: (v) => v.style === 'regular'
          },
          {
            name: 'sort_order',
            label: 'Where it sits on Home',
            type: 'number'
          },
          {
            name: 'status',
            label: 'Availability',
            type: 'select',
            required: true,
            // The preview draws all three states, which is a better answer
            // than describing them. The one consequence it cannot draw is the
            // re-ordering -- an Upcoming subject sits below every bookable one
            // whatever its number -- which the help line used to say and which
            // now lives only here (2026-10-22).
            options: CATEGORY_STATUSES
          }
        ]}
        emptyValues={{
          status: 'open',
          sort_order: 0,
          page_key: '',
          style: 'module'
        }}
        formToPayload={(v) => ({
          ...v,
          sort_order: Number(v.sort_order) || 0,
          // A cleared box must post 0, not ''. DRF rejects '' for a decimal,
          // so without this, blanking the discount fails the whole save with
          // an error about a field the admin did not think they were editing.
          trial_discount: Number(v.trial_discount) || 0
        })}
      />
    </div>
  )
}

/** Setup → Pricing tab: module price per category × sessions a week. */
export const AtHomePricingSetup = () => {
  const classes = useStyles()
  const [categories, setCategories] = React.useState([])
  React.useEffect(() => {
    fetchAtHomeCategories()
      .then(setCategories)
      .catch(() => {})
  }, [])
  const priced = categories.filter((c) => c.status !== 'inactive')
  const styleOf = (uuid) => categories.find((c) => c.uuid === uuid)?.style
  return (
    <div className={classes.root}>
      <SetupList
        hint="One price for each category and each number of sessions a week. For a category sold as projects it is the price of a whole project; for a monthly category it is one month's fee. The price is copied onto the booking the moment it is made, so editing it here never changes a booking already made."
        entityLabel="Price"
        fetchList={fetchAtHomePricing}
        createItem={createAtHomePricing}
        updateItem={updateAtHomePricing}
        deleteItem={deleteAtHomePricing}
        columns={[
          { name: 'categoryName', label: 'Category' },
          {
            name: 'frequency',
            label: 'Sessions a week',
            render: (r) => freqLabel(r.frequency, styleOf(r.category))
          },
          {
            name: 'price',
            label: 'Price before GST',
            align: 'right',
            render: (r) => `₹${Number(r.price).toLocaleString('en-IN')}`
          }
        ]}
        fields={[
          {
            name: 'category',
            label: 'Category',
            type: 'select',
            required: true,
            options: priced.map((c) => ({
              value: c.uuid,
              label: categoryLabel(c)
            }))
          },
          {
            // The "Once a week / Twice a week" tiles in the app are hard-coded
            // wording, not these rows; the tile a parent taps is what decides
            // which of the two rows they are charged.
            name: 'freqNote',
            type: 'note',
            label:
              'A parent chooses “Once a week” or “Twice a week” in the app, and that choice decides which of these two rows they are charged. Only the once-a-week price is advertised on the subject page.'
          },
          {
            // Deliberately NOT freqLabel() here. This dialog cannot know the
            // picked category's style (the options are a fixed array), and
            // freqLabel defaults to 'module' -- so every Monthly row was
            // labelled "8 sessions over 8 weeks" when it is one month's fee,
            // and the "8" is hard-coded while a project's session count is
            // editable. The note above carries the meaning instead.
            name: 'frequency',
            label: 'Sessions a week',
            type: 'select',
            required: true,
            options: [
              { value: '1', label: 'Once a week' },
              { value: '2', label: 'Twice a week' }
            ]
          },
          {
            name: 'priceNote',
            type: 'note',
            label:
              'For a category sold as projects this is the whole project, all its sessions together; for a monthly category it is one month. It is the amount before tax: 18% GST is added at checkout, so it is never the figure a parent is charged. The app prints it as “₹X + GST” on the subject page and spells out the total on the payment screen.'
          },
          {
            name: 'price',
            label: 'Price (₹) before GST',
            type: 'number',
            required: true
          }
        ]}
        emptyValues={{ frequency: '1', price: '' }}
        formToPayload={(v) => ({
          category: v.category,
          frequency: Number(v.frequency),
          price: Number(v.price)
        })}
      />
    </div>
  )
}

/** Setup → Modules tab: the named 8-session curricula inside each module-style category. */
export const AtHomeModulesSetup = () => {
  const classes = useStyles()
  const [categories, setCategories] = React.useState([])
  React.useEffect(() => {
    fetchAtHomeCategories()
      .then(setCategories)
      .catch(() => {})
  }, [])
  const moduleCategories = categories.filter(
    (c) => c.status !== 'inactive' && c.style === 'module'
  )
  const fetchList = React.useCallback(() => fetchAtHomeModules(), [])
  return (
    <div className={classes.root}>
      <SetupList
        /* Trimmed with the form, 2026-10-19: what a project IS, and nothing
           more. Everything this used to spell out — the session plan, the
           grades, the 8 — is now one line under the field it belongs to, or
           drawn in the preview. */
        hint="The named projects a parent picks inside a subject — STEAM “Home Helpers”, Music “Foundations of Music 1”. They are the cards on a subject's page."
        entityLabel="Project"
        formMaxWidth="md"
        formPreview={(values) => <ProjectPreview values={values} />}
        fetchList={fetchList}
        createItem={createAtHomeModule}
        updateItem={updateAtHomeModule}
        deleteItem={deleteAtHomeModule}
        columns={[
          { name: 'categoryName', label: 'Category' },
          { name: 'name', label: 'Project' },
          { name: 'gradesLabel', label: 'Grades' },
          {
            name: 'sessions',
            label: 'Session plan',
            render: (r) =>
              `${(r.sessions || []).filter((x) => x.n >= 1).length}/${
                r.sessions_count || 8
              } sessions`
          },
          { name: 'sort_order', label: 'Order', align: 'right' },
          {
            name: 'status',
            label: 'Status',
            render: (r) => statusLabel(MODULE_STATUSES, r.status)
          }
        ]}
        fields={[
          {
            name: 'category',
            label: 'Category',
            type: 'select',
            required: true,
            options: moduleCategories.map((c) => ({
              value: c.uuid,
              label: categoryLabel(c)
            })),
            help: 'Only categories sold as Projects are listed. Monthly ones have no projects.'
          },
          {
            name: 'name',
            label: 'Project name',
            type: 'text',
            required: true,
            // The colon rule is the surprising one, so it leads. The permanent
            // id is a once-ever consequence of the first save and does not
            // belong in a hint you read every time — the preview shows the
            // split, which is what you actually need while typing.
            help: 'Anything after a colon becomes the card’s second line — see the card on the right.'
          },
          {
            name: 'image_url',
            label: 'Photo on the card',
            type: 'image',
            uploadLabel: 'photo',
            help: 'Leave blank to keep the app’s own drawing for this project.'
          },
          {
            // CORRECTED, 2026-10-19. This said "stored, but no app screen shows
            // it". It is the intro paragraph at the top of the project page
            // (at-home-app .../module/[id].tsx:496), and the typed value WINS
            // over the app's bundled line. All 12 projects on prod have it
            // filled, so anyone who believed the old label and cleared the box
            // would have deleted live copy from twelve pages.
            name: 'blurb',
            label: 'One-line intro',
            type: 'text',
            help: 'The paragraph at the top of the project page. Blank = the app’s own line.'
          },
          {
            name: 'grades',
            label: 'Classes this project is for',
            type: 'multiselect',
            options: GRADES,
            help: 'Never shown to a parent — it is checked at checkout. Empty = every class.'
          },
          {
            name: 'status',
            label: 'Availability',
            type: 'select',
            required: true,
            options: MODULE_STATUSES,
            // The card stays on the page for BOTH non-open values: the parent
            // modules endpoint applies no status filter and the app draws
            // `status !== 'open'` as the same stamp. Said here because the
            // option wording cannot carry it.
            help: 'Coming soon and Inactive both leave the card on the subject page, stamped “Coming soon”. Neither removes it from the app.'
          },
          {
            name: 'sort_order',
            label: 'Order in the list',
            type: 'number',
            help: 'Lower comes first — but only for a subject whose built-in write-up has no project list of its own. Where it has one, the app’s order wins.'
          },
          {
            name: 'sessions',
            type: 'custom',
            render: ({ values, setField }) => (
              <SessionPlanField values={values} setField={setField} />
            )
          }
        ]}
        emptyValues={{
          status: 'open',
          sort_order: 0,
          grades: [],
          sessions_count: 8,
          sessions: []
        }}
        formToPayload={(v) => {
          /**
           * SHORTENING A PROJECT (owner, 2026-10-21).
           *
           * `SessionPlanField` draws rows 1..sessions_count, but it never
           * removed the rows past that from `values.sessions` -- so taking a
           * project from 8 sessions to 5 left rows 6, 7 and 8 in the form,
           * invisible, and posted them. The backend refuses exactly that
           * ("The session plan has a row beyond session 5"), so the Save
           * failed and the count could not be reduced at all.
           *
           * Trimmed HERE rather than when the number changes: the count is a
           * text input, so every keystroke is a value -- typing "12" passes
           * through "1", and trimming on change would delete eleven rows on
           * the way. At Save the number is the one the editor settled on, and
           * what is sent matches the rows they were shown.
           */
          const count = Number(v.sessions_count) || 8
          return {
          category: v.category,
          name: v.name,
          image_url: v.image_url || '',
          blurb: v.blurb || '',
          grades: v.grades || [],
          status: v.status,
          sort_order: Number(v.sort_order) || 0,
          sessions_count: count,
          sessions: (v.sessions || [])
            .filter((r) => r.n >= 1 && r.n <= count && (r.name || '').trim())
            .map((r) => ({
              n: r.n,
              name: r.name.trim(),
              // EVERY FIELD THE ROW HOLDS, not just the three the table used to
              // edit. This built {n, name, description} only, so each Save sent
              // a row stripped of `concept`, `explores` and `helps` -- and since
              // `sessions` is written whole, Alfred was deleting copy nobody had
              // touched. `concept` is the CONCEPT chip on the session card.
              // `explores` is NO LONGER DRAWN to a parent and no longer has a
              // column (owner, 2026-10-22) -- it stays in this map precisely
              // because `sessions` is written whole: leaving it out is what
              // would erase the stored copy, which is not what was asked for.
              //
              // `topic` IS NO LONGER SENT (owner, 2026-10-22): the column is
              // gone from `SessionPlanField` and grouping is on the session
              // title. Because `sessions` is written whole, leaving it out
              // here is what CLEARS it from a row that still carries one --
              // which is the intent, and which cost nothing: 0 of 12 modules
              // on prod had a topic set. The rule above still stands for every
              // other key: anything the editor can type belongs in this map,
              // or Save deletes it silently.
              concept: (r.concept || '').trim(),
              description: (r.description || r.helps || '').trim(),
              explores: (r.explores || '').trim(),
              helps: (r.helps || '').trim()
            }))
          }
        }}
      />
    </div>
  )
}
